import React, { useState, useEffect, useRef } from "react";
import { Row, Col } from "react-flexbox-grid";
import { connect } from "react-redux";
import { postRequest } from "../../actions/gameAction";
import { GIFTING_DATA } from "../../constants/gameConstants";
import { SENDING_REWARDS } from "../../constants/accountConstants";
import pusher from "../../constants/pusher";
import { FormGroup, Label, Input, FormFeedback, Container } from "reactstrap";
import { Field, reduxForm, change, reset, getFormValues } from "redux-form";
import Swal from "sweetalert2";
import { checkUserIsLogin } from "../../actions/loginActions";
import { HelpSection } from "../Utils/HelpSection";
import ChatModal from ".././ChatModal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const validate = (values) => {
  const errors = { gift: {} };
  if (values["gift"] && !values["gift"]["value"])
    errors["gift"]["value"] = "This field is required";

  return errors;
};

const renderSelectField = ({ input, label, meta: { error }, ...custom }) => (
  <FormGroup className="custom-form-field-w-label-white">
    <Label>{label}</Label>
    <Input {...input} {...custom} type="select">
      {custom.options}
    </Input>
    <FormFeedback>{error}</FormFeedback>
  </FormGroup>
);

const renderTextField = ({
  input,
  label,
  meta: { touched, error },
  ...custom
}) => (
  <FormGroup className="custom-form-field-w-label-white">
    <Label>{label}</Label>
    <Input
      {...input}
      {...custom}
      onChange={input.onChange}
      value={input.value}
      type={custom.type}
      invalid={touched && error ? true : false}
      placeholder={custom.placeholder}
      style={{ color: "#ffca27" }}
    />
    <FormFeedback>{error}</FormFeedback>
  </FormGroup>
);

// Without type="checkbox" on the <Field>, redux-form populates input.value
// (the boolean) but not input.checked, so spreading {...input} only sets the
// DOM `value` attribute — the `checked` attribute (and the :checked CSS
// selector) stays untouched. Force `checked` from input.value here.
// Also chain input.onChange with any custom onChange passed via the Field
// since spreading {...custom} after {...input} would otherwise drop
// redux-form's onChange and leave checkbox state out of the form.
const renderCheckBoxField = ({
  input,
  label,
  meta: { touched, error },
  onChange: customOnChange,
  ...custom
}) => (
  <FormGroup className="game-config-checkbox-css" style={{ color: "grey" }}>
    <Label style={{ margin: "0.4rem 1rem", fontSize: "large" }}>{label}</Label>
    <Input
      {...input}
      {...custom}
      type="checkbox"
      checked={!!input.value}
      style={{ color: "#ffca27" }}
      onChange={(e) => {
        input.onChange(e);
        if (customOnChange) customOnChange(e);
      }}
    />
    <FormFeedback>{error}</FormFeedback>
  </FormGroup>
);

const GIFT_OPTIONS = ["Picks", "Points", "Tickets", "Spiffs"];
const giftNames = GIFT_OPTIONS.map((state, index) => {
  return <option key={index}> {state} </option>;
});

const LiveGifting = (props) => {
  const [options, setOptions] = useState("Tickets");
  const [showSpiffField, setShowSpiffField] = useState(false);
  const [showSpiffValue, setShowSpiffValue] = useState(false);
  const [showSpiffCheckBox, setShowSpiffCheckBox] = useState(true);
  const [showProceed, setShowProceed] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const runOnceRef = useRef(true);
  const notifyOnceRef = useRef(true);
  const spiffValueRef = useRef(null);

  const noticeAlert = (text) => {
    toast.success(text, {
      position: "top-right",
      autoClose: 60000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: true,
      draggablePercent: 40,
      className: "toaster-css-2",
    });
  };

  const loadGameData = (gameData) => {
    if (gameData.game) {
      const channel_name = "games_" + gameData.game.id;
      pusher.unsubscribe(channel_name);
      const channel = pusher.subscribe(channel_name);
      channel.bind("game_event", (data) => {
        switch (data.type) {
          case "new_message":
            if (data.data.message_to == "Game Host")
              noticeAlert(
                " 💬 " +
                  data.data.message_from +
                  " : " +
                  data.data.message +
                  " ",
              );
            break;
        }
      });
    }
  };

  // Mount: kick off auth check and the initial gifting data fetch.
  useEffect(() => {
    props.dispatch(checkUserIsLogin());
    props.dispatch(
      postRequest("games/gifting_component", {
        type: GIFTING_DATA,
        values: { game: { code: props.match.params.game_code } },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replaces UNSAFE_componentWillReceiveProps — runs after every render and
  // gates work via the refs the same way the class did.
  useEffect(() => {
    if (props.game && props.game.game && runOnceRef.current) {
      runOnceRef.current = false;
      loadGameData(props.game);
    }
    if (props.account.giftStatus && notifyOnceRef.current) {
      notifyOnceRef.current = false;
      setShowSpiffField(false);
      setIsSending(false);
      setOptions("Tickets");
      // Clear all checkboxes once the gift is actually delivered. reset()
      // only restores fields present in initialValues; player checkboxes are
      // dynamic and absent from initialValues so they must be cleared manually.
      // SELECT ALL is a derived/controlled checkbox so it follows automatically.
      props.dispatch(reset("giftForm"));
      const { players } = props.game || {};
      if (players) {
        players.forEach((player) =>
          props.dispatch(change("giftForm", `gift.${player.id}`, false)),
        );
      }
      Swal.fire({
        position: "center",
        type: "success",
        title: props.account.giftStatus,
        showConfirmButton: false,
        timer: 1500,
      });
    }
  });

  const getHelp = () => {
    HelpSection();
  };

  const pusherRequest = (event) => {
    props.dispatch(
      postRequest("games/pusher_update", {
        values: {
          game: {
            code: props.match.params.game_code,
            status: event,
            spiff_value: spiffValueRef.current,
          },
        },
      }),
    );
  };

  const giftValues = (values) => {
    setIsSending(true);
    props.dispatch(
      postRequest("player/send_rewards", {
        type: SENDING_REWARDS,
        values: { game: { code: props.match.params.game_code }, ...values },
      }),
    );
    notifyOnceRef.current = true;
  };

  // Check / uncheck every player checkbox to match the SELECT ALL state.
  // Each dispatch updates the corresponding redux-form Field so it re-renders
  // with checked=true/false from the new form state.
  const handleSelectAll = (checked) => {
    const { players } = props.game || {};
    if (!players || !players.length) return;
    players.forEach((player) => {
      props.dispatch(change("giftForm", `gift.${player.id}`, checked));
    });
  };

  const pptsOptions = (val) => {
    props.dispatch(reset("giftForm"));
    props.dispatch(change("giftForm", "gift.value", ""));
    setOptions(val);
  };

  const setSpiffValue = (data) => {
    if (data.spiff_value) {
      spiffValueRef.current = data.spiff_value;
      props.dispatch(change("giftForm", "spiffCheckBox", false));
      setShowSpiffField(false);
      setShowSpiffValue(true);
      setShowSpiffCheckBox(false);
      Swal.fire({
        type: "success",
        title: "Prize value set Successfully",
        showConfirmButton: false,
        timer: 1500,
      });
    } else {
      Swal.fire({
        type: "warning",
        title: "Prize value is required",
        showConfirmButton: false,
        timer: 1500,
      });
    }
  };

  const { handleSubmit } = props;
  const { players, series } = props.game;
  // SELECT ALL is derived from the actual player Field values, so it stays
  // in sync regardless of which way the user got there (clicked SELECT ALL,
  // or toggled each player one by one).
  const giftVals = (props.formValues && props.formValues.gift) || {};
  const allPlayersSelected = !!(
    players &&
    players.length > 0 &&
    players.every((p) => !!giftVals[p.id])
  );

  return (
    <Container>
      <Row>
        <Col
          xs={12}
          md={8}
          mdOffset={2}
          style={{ background: "#fff", padding: "1rem" }}
        >
          <Row center="xs">
            <Col xs>
              <a
                onClick={() => {
                  getHelp();
                }}
              >
                <i className="fa fa-life-ring" /> Host Help
              </a>
            </Col>
            <Col xs>
              <a
                onClick={() => {
                  setShowChatModal(true);
                }}
              >
                <i className="fa fa-comments-o" aria-hidden="true"></i> Game
                Chat
              </a>
            </Col>
          </Row>
          {showChatModal && (
            <ChatModal
              code={props.match.params.game_code}
              closeModal={() => setShowChatModal(false)}
            />
          )}
          <Row>
            <Col xs>
              <a
                onClick={() => window.history.back()}
                style={{
                  color: "#ffca27",
                  textDecoration: "none",
                  fontWeight: "bold",
                  lineHeight: "3.5",
                }}
              >
                <i className="fa fa-arrow-left" /> BACK TO THE GAME
              </a>
            </Col>
          </Row>
          <h5 style={{ color: "#241b44", fontWeight: "bolder" }}>
            {series && series.name ? series.name : "NO SERIES"}
          </h5>
          <p style={{ margin: "0.3rem", fontSize: "0.8rem", color: "grey" }}>
            Active Session:{" "}
            {series && series.active ? series.active : "NO SESSION"}
          </p>
          <h3 className="bold" style={{ marginTop: "0.8rem" }}>
            Gifts
          </h3>
          <p style={{ color: "grey", marginTop: "0.5rem", fontSize: "0.8rem" }}>
            Select the type of gift,amount,and the recipients below
          </p>
          <form onSubmit={handleSubmit(giftValues)}>
            <Field
              name="gift.type"
              component={renderSelectField}
              label="SELECT GIFT TYPE"
              options={giftNames}
              onChange={(p) => pptsOptions(p.target.value)}
            />
            {options == "Tickets" && (
              <Field
                name="gift.value"
                component={renderTextField}
                type="number"
                min="1"
                max="99"
                maxLength="4"
                label="Ticket Count"
                autoFocus
              />
            )}
            {options == "Spiffs" && (
              <Field
                name="gift.value"
                component={renderTextField}
                label="Spiff Type"
                autoFocus
              />
            )}
            {options == "Points" && (
              <Field
                name="gift.value"
                component={renderTextField}
                label="AMOUNT"
                autoFocus
              />
            )}
            {players && players.length != 0 && (
              <FormGroup
                className="game-config-checkbox-css"
                style={{ color: "grey" }}
              >
                <Label style={{ margin: "0.4rem 1rem", fontSize: "large" }}>
                  SELECT ALL
                </Label>
                <Input
                  type="checkbox"
                  style={{ color: "#ffca27" }}
                  checked={allPlayersSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </FormGroup>
            )}
            {players &&
              players.length != 0 &&
              players.map((x) => (
                <Field
                  key={x.id}
                  name={`gift.${x.id}`}
                  component={renderCheckBoxField}
                  label={x.name}
                />
              ))}
            <button
              className="mayhem-btn-blue btn-full-width"
              type="submit"
              style={{ marginTop: "0.5rem" }}
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <i
                    className="fa fa-spinner fa-spin"
                    style={{ marginRight: "0.5rem" }}
                  />
                  SENDING...
                </>
              ) : (
                "SEND GIFT"
              )}
            </button>
          </form>
          <hr />
          <h3 className="bold" style={{ marginTop: "0.8rem" }}>
            Raffles
          </h3>
          <p style={{ color: "grey", marginTop: "0.5rem", fontSize: "0.8rem" }}>
            Set an award for the drawing and spin the wheel!
          </p>
          <button
            className="mayhem-btn-blue btn-full-width"
            style={{ backgroundColor: "#ed4e6b", marginTop: "3rem" }}
            onClick={() => {
              pusherRequest("showPpt");
              setShowProceed(true);
            }}
          >
            <i
              style={{
                fontSize: "1.8rem",
                position: "relative",
                right: "10px",
                top: "3px",
              }}
              className="fa fa-ticket"
              aria-hidden="true"
            />
            DO A DRAWING
          </button>
          {showSpiffCheckBox && (
            <Field
              id="spiffCheckBox"
              name="spiffCheckBox"
              component={renderCheckBoxField}
              label="SET DRAWING PRIZE"
              onChange={() => {
                setShowSpiffField((prev) => !prev);
                setShowSpiffValue(false);
              }}
            />
          )}
          {showSpiffField && (
            <form onSubmit={handleSubmit(setSpiffValue)}>
              <Row>
                <Col xs={6} style={{ background: "#fff", padding: "1rem" }}>
                  <Field
                    style={{ width: "50%" }}
                    name="spiff_value"
                    component={renderTextField}
                    label="ENTER PRIZE VALUE"
                    autoFocus
                  />
                </Col>
                <Col xs={6} style={{ background: "#fff", padding: "1rem" }}>
                  <button
                    className="mayhem-btn-blue btn-full-width"
                    type="submit"
                    style={{ height: "80%" }}
                  >
                    SET PRIZE VALUE
                  </button>
                </Col>
              </Row>
            </form>
          )}
          {showSpiffValue && (
            <div>
              Prize Value : {spiffValueRef.current}
              <button
                type="button"
                className="fa fa-remove"
                style={{
                  top: "-4rem",
                  background: " none",
                  border: "none",
                  color: "black",
                }}
                onClick={() => {
                  spiffValueRef.current = null;
                  props.dispatch(change("giftForm", "spiffCheckBox", false));
                  setShowSpiffCheckBox(true);
                  setShowSpiffValue(false);
                }}
              />
            </div>
          )}
          {showProceed && (
            <button
              className="mayhem-btn-blue btn-full-width"
              style={{ backgroundColor: "#ed4e6b", marginTop: "1rem" }}
              onClick={() => {
                pusherRequest("openSlotMachine");
                setShowProceed(false);
              }}
            >
              SPIN NOW!
            </button>
          )}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              style={{ marginLeft: "0" }}
              className="close-ticket-btn"
              onClick={() => {
                pusherRequest("markTicketAsRedeemed");
                setShowProceed(false);
              }}
            >
              <i
                style={{
                  fontSize: "1.6rem",
                  position: "relative",
                  right: "5px",
                  top: "3px",
                }}
                className="fa fa-check"
                aria-hidden="true"
              />
              TICKET REDEEMED
            </button>
            <button
              style={{ marginLeft: "0" }}
              className="close-ticket-btn"
              onClick={() => pusherRequest("reSpinSlotMachine")}
            >
              <i
                style={{
                  fontSize: "1.6rem",
                  position: "relative",
                  right: "5px",
                  top: "2px",
                }}
                className="fa fa-refresh"
                aria-hidden="true"
              />
              RESPIN
            </button>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

const GiftForm = reduxForm({
  form: "giftForm",
  validate,
  enableReinitialize: true,
})(LiveGifting);

function mapStateToProps(store) {
  return {
    auth: store.auth,
    game: store.game,
    account: store.account,
    formValues: getFormValues("giftForm")(store),
    initialValues: { gift: { type: "Tickets", value: "1" } },
  };
}

export default connect(mapStateToProps)(GiftForm);
